import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useTRPC } from "@/trpc";
import { ProviderCredentialsSheetV2 } from "@/routes/_org/org/bu/$organizationBusinessUnitId/providers/credentials/_components/provider-credentials-sheet-v2";
import {
  WorkspaceRosterEditDialog,
  type WorkspaceRosterEditorMode,
} from "../../workspace-roster-editor";
import type { WorkspaceProvider } from "../../workspace-types";
import type { CredentialVaultTab } from "@/features/credentials/components/credential-vault-view";

/** Roster authority supplies only draft personal/contact edits. General native
 * credential editing continues to use the credential service's access result. */
export function NativeDocuments({
  provider,
  organizationBusinessUnitId,
  view,
}: {
  provider: WorkspaceProvider;
  organizationBusinessUnitId: string;
  view: CredentialVaultTab;
}) {
  const trpc = useTRPC();
  const canManageDraft =
    !provider.linked && provider.capabilities.canChangeEmployment;
  const snapshot = useQuery({
    ...trpc.providerWorkspace.rosterDetails.queryOptions({
      organizationBusinessUnitId,
      affiliationId: provider.affiliationId,
      providerUserId: provider.providerUserId,
    }),
    enabled: canManageDraft,
  });
  const [editor, setEditor] = useState<WorkspaceRosterEditorMode | null>(null);
  const canEditRoster = canManageDraft && !!snapshot.data && !snapshot.isError;
  const summary = {
    id: provider.providerUserId,
    firstName: provider.firstName ?? "",
    lastName: provider.lastName ?? "",
    role: provider.role ?? "",
    specialty: provider.specialty ?? "",
    isRevealed: true,
    documents: [],
  };
  let contact = null;
  if (canManageDraft && snapshot.isError) {
    contact = (
      <section aria-label="Contact" className="space-y-2">
        <h4 className="text-sm font-semibold text-slate-800">Contact</h4>
        <p role="alert" className="text-sm text-destructive">
          Couldn’t load the organization’s contact details.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void snapshot.refetch()}
        >
          Try again
        </Button>
      </section>
    );
  } else if (canEditRoster) {
    contact = (
      <section
        aria-label="Contact"
        className="space-y-3 border-t border-slate-200 pt-4"
      >
        <div className="flex items-center justify-between gap-3">
          <h4 className="text-sm font-semibold text-slate-800">Contact</h4>
          <Button
            variant="outline"
            size="sm"
            className="h-9 rounded-xl text-xs text-primary"
            onClick={() => setEditor("phone")}
          >
            Edit phone
          </Button>
        </div>
        <dl className="text-sm">
          <dt className="text-slate-500">Phone</dt>
          <dd className="mt-1 text-slate-900">
            {snapshot.data?.invitation.phone || "Not provided"}
          </dd>
        </dl>
        <p className="text-xs text-slate-500">
          Manage invitation email addresses in Invitations.
        </p>
      </section>
    );
  }
  return (
    <ProviderCredentialsSheetV2
      embedded
      view={view}
      provider={summary}
      providerUserId={provider.providerUserId}
      expectedAffiliationId={provider.affiliationId}
      organizationBusinessUnitId={organizationBusinessUnitId}
      primarySourceVerificationEnabled={
        provider.primarySourceVerificationEnabled
      }
      onEditPersonalDetails={
        canEditRoster ? () => setEditor("personal") : undefined
      }
      personalDetailsContent={contact}
      isDetailsLoading={canManageDraft && snapshot.isPending}
      additionalDialogOpen={canEditRoster && editor !== null}
      open
      onOpenChange={noop}
    >
      {canEditRoster && editor && (
        <WorkspaceRosterEditDialog
          provider={provider}
          organizationBusinessUnitId={organizationBusinessUnitId}
          mode={editor}
          onClose={() => setEditor(null)}
        />
      )}
    </ProviderCredentialsSheetV2>
  );
}

function noop() {}
